import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { tenantAPI } from '../../services/api';
import toast from 'react-hot-toast';

const InspectionForm = ({ inspection, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [assigneeId, setAssigneeId] = useState('');
  const [photos, setPhotos] = useState([]);

  // 获取租户下的用户列表
  const { data: usersData, isLoading: isLoadingUsers, error: usersError, refetch: refetchUsers } = useQuery(
    'tenantUsers',
    () => tenantAPI.getUsers(),
    {
      enabled: true,
      retry: 1,
      staleTime: 0,
      cacheTime: 0,
      onError: (err) => {
        console.error('获取用户列表失败:', err);
        toast.error('获取用户列表失败: ' + (err.response?.data?.error || err.message));
      }
    }
  );

  const users = usersData?.users || [];

  useEffect(() => {
    if (inspection) {
      // 编辑模式：设置表单初始值
      reset({
        title: inspection.title,
        description: inspection.description || '',
        location: inspection.location || '',
        area: inspection.area || '',
        priority: inspection.priority || 'medium',
        status: inspection.status || 'pending',
        due_date: inspection.due_date ? new Date(inspection.due_date).toISOString().split('T')[0] : '',
        findings: inspection.findings || '',
        recommendations: inspection.recommendations || '',
      });
      setAssigneeId(inspection.assignee?.id || '');
      setPhotos(inspection.photos || []);
    } else {
      // 创建模式：重置表单
      reset({
        title: '',
        description: '',
        location: '',
        area: '',
        priority: 'medium',
        status: 'pending',
        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 默认7天后
        findings: '',
        recommendations: '',
      });
      setAssigneeId('');
      setPhotos([]);
    }
  }, [inspection, reset]);

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) {
      toast.error('最多只能上传5张照片');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = (data) => {
    if (!assigneeId) {
      toast.error('请选择负责人');
      return;
    }

    const formData = {
      ...data,
      assignee_id: parseInt(assigneeId),
      due_date: data.due_date ? new Date(data.due_date).toISOString() : null,
      photos: photos,
    };

    // 如果是创建模式且没有填写findings/recommendations，设置为空字符串
    if (!inspection) {
      if (!formData.findings) formData.findings = '';
      if (!formData.recommendations) formData.recommendations = '';
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 任务名称 */}
        <div className="md:col-span-2">
          <label className="form-label">任务名称 *</label>
          <input
            type="text"
            {...register('title', { required: '请输入任务名称' })}
            className="form-input"
            placeholder="请输入巡检任务名称"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-danger">{errors.title.message}</p>
          )}
        </div>

        {/* 负责人 */}
        <div>
          <label className="form-label">负责人 *</label>
          {isLoadingUsers ? (
            <div className="form-input text-gray-500 flex items-center">
              <div className="spinner mr-2" style={{width: '16px', height: '16px', borderWidth: '2px'}}></div>
              加载用户列表...
            </div>
          ) : usersError ? (
            <div className="form-input text-red-500 flex items-center justify-between">
              <span>加载失败</span>
              <button
                type="button"
                onClick={() => refetchUsers()}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                点击重试
              </button>
            </div>
          ) : (
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="form-input"
              required
            >
              <option value="">请选择负责人</option>
              {users.length === 0 ? (
                <option value="" disabled>暂无可用用户</option>
              ) : (
                users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.username} ({user.department || user.position || '无部门'})
                  </option>
                ))
              )}
            </select>
          )}
        </div>

        {/* 优先级 */}
        <div>
          <label className="form-label">优先级</label>
          <select
            {...register('priority')}
            className="form-input"
          >
            <option value="low">低</option>
            <option value="medium">中</option>
            <option value="high">高</option>
          </select>
        </div>

        {/* 巡检区域 */}
        <div>
          <label className="form-label">巡检区域</label>
          <input
            type="text"
            {...register('area')}
            className="form-input"
            placeholder="如：电气安全、消防安全等"
          />
        </div>

        {/* 具体位置 */}
        <div>
          <label className="form-label">具体位置</label>
          <input
            type="text"
            {...register('location')}
            className="form-input"
            placeholder="如：生产区-配电房"
          />
        </div>

        {/* 截止时间 */}
        <div>
          <label className="form-label">截止时间</label>
          <input
            type="date"
            {...register('due_date')}
            className="form-input"
          />
        </div>

        {/* 状态（仅在编辑时显示） */}
        {inspection && (
          <div>
            <label className="form-label">状态</label>
            <select
              {...register('status')}
              className="form-input"
            >
              <option value="pending">待下发</option>
              <option value="processing">待处理</option>
              <option value="reviewing">待审批</option>
              <option value="completed">已完成</option>
              <option value="archived">已归档</option>
            </select>
          </div>
        )}

        {/* 描述 */}
        <div className="md:col-span-2">
          <label className="form-label">任务描述</label>
          <textarea
            {...register('description')}
            rows="3"
            className="form-input"
            placeholder="请输入巡检任务的详细描述和要求"
          />
        </div>

        {/* 检查发现（仅在编辑时显示） */}
        {inspection && (
          <div className="md:col-span-2">
            <label className="form-label">检查发现</label>
            <textarea
              {...register('findings')}
              rows="3"
              className="form-input"
              placeholder="记录检查中发现的问题"
            />
          </div>
        )}

        {/* 处理建议（仅在编辑时显示） */}
        {inspection && (
          <div className="md:col-span-2">
            <label className="form-label">处理建议</label>
            <textarea
              {...register('recommendations')}
              rows="3"
              className="form-input"
              placeholder="提出处理建议和整改措施"
            />
          </div>
        )}

        {/* 巡检照片上传 */}
        <div className="md:col-span-2">
          <label className="form-label">巡检照片 (最多5张)</label>
          <div className="flex flex-wrap gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="relative w-24 h-24">
                <img src={photo} alt={`巡检照片${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  className="hidden"
                  id="inspection-photos"
                />
                <label htmlFor="inspection-photos" className="cursor-pointer text-center">
                  <i className="fa fa-camera text-gray-400 text-xl"></i>
                  <p className="text-xs text-gray-500 mt-1">添加照片</p>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 表单操作按钮 */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              {inspection ? '保存中...' : '创建中...'}
            </>
          ) : (
            inspection ? '保存更改' : '创建任务'
          )}
        </button>
      </div>
    </form>
  );
};

export default InspectionForm;